import { Combobox } from '@base-ui/react/combobox'
import type { Payer } from '@cartwheel/api/v1'
import { useId } from 'react'

interface PayerComboboxProps {
  payers: Payer[]
  value: string | null
  onChange: (payerId: string | null) => void
  disabled?: boolean
  placeholder?: string
  label: string
}

export default function PayerCombobox({
  payers,
  value,
  onChange,
  disabled = false,
  placeholder = 'Search for a payer...',
  label,
}: PayerComboboxProps) {
  const id = useId()
  const currentPayer = payers.find((p) => p.id === value)

  return (
    <Combobox.Root
      items={payers}
      value={currentPayer || null}
      onValueChange={(newPayer) => onChange(newPayer?.id || null)}
      disabled={disabled}
      itemToStringLabel={(payer) => payer?.name ?? ''}
      itemToStringValue={(payer) => payer?.id ?? ''}
    >
      <div>
        <label htmlFor={id} className="block mb-2 text-sm font-medium text-charcoal-50">
          {label}
        </label>

        <div className="relative">
          <Combobox.Input
            id={id}
            placeholder={placeholder}
            className="bg-white border border-gray-stroke focus:border-lavender h-10 w-full rounded-lg px-4 pr-20 focus:ring-0 text-sm data-disabled:opacity-40"
          />

          <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
            <Combobox.Clear
              className="h-6 w-6 flex items-center justify-center text-charcoal-50 hover:text-charcoal-75 data-disabled:opacity-40"
              aria-label="Clear selection"
            >
              <i className="fa-solid fa-xmark text-xs"></i>
            </Combobox.Clear>

            <Combobox.Trigger
              className="h-6 w-6 flex items-center justify-center text-charcoal-50 data-disabled:opacity-40"
              aria-label="Open payer list"
            >
              <i className="fa-solid fa-caret-down"></i>
            </Combobox.Trigger>
          </div>
        </div>
      </div>

      <Combobox.Portal>
        <Combobox.Positioner align="start" className="outline-none z-10 select-none" sideOffset={4}>
          <Combobox.Popup className="box-border border rounded-lg border-gray-stroke bg-white shadow-lg w-[var(--anchor-width)] origin-[var(--transform-origin)] overflow-y-auto max-h-[var(--available-height)]">
            <Combobox.Empty className="px-4 py-8 text-center text-sm text-charcoal-50">
              No payers found.
            </Combobox.Empty>

            <Combobox.List className="py-1">
              {(payer: Payer) => (
                <Combobox.Item
                  key={payer.id}
                  value={payer}
                  className="px-4 py-2 cursor-default select-none outline-none data-highlighted:bg-sand data-selected:text-forest"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm">{payer.name}</span>
                    <Combobox.ItemIndicator className="ml-2">
                      <i className="fa-solid fa-check text-xs text-forest"></i>
                    </Combobox.ItemIndicator>
                  </div>
                </Combobox.Item>
              )}
            </Combobox.List>
          </Combobox.Popup>
        </Combobox.Positioner>
      </Combobox.Portal>
    </Combobox.Root>
  )
}
